import {Disclosure} from '@github-ui/disclosure'
import {clsx} from 'clsx'
import {use, useState} from 'react'
import type {ReactNode, Ref} from 'react'
import {useCollapsibleSidebarContext} from '../CollapsibleSidebar/CollapsibleSidebar'
import {SidebarAction} from './Action'
import actionStyles from './Action.module.css'
import {GroupedContext} from './sidebar-context'
import type {ChildrenProps} from './sidebar-context'
import {DisclosurePanel} from './DisclosurePanel'
import rowStyles from './Row.module.css'
import styles from './Accordion.module.css'
import {containsCurrent, sidebarChildKind} from './contains-current'

/** Nested disclosure within a titled Section; use a collapsible Section for top-level disclosure. */
export interface AccordionProps extends ChildrenProps {
  title: string
  defaultOpen?: boolean
  action?: ReactNode
  className?: string
  ref?: Ref<HTMLButtonElement>
}

export const Accordion = Object.assign(
  function SidebarAccordion({title, defaultOpen, action, className, children, ref}: AccordionProps) {
    const grouped = use(GroupedContext)
    const {visualIsExpanded} = useCollapsibleSidebarContext()
    const hasCurrent = containsCurrent(children)
    const [open, setOpen] = useState(() => defaultOpen ?? hasCurrent)
    if (!grouped) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          '[Sidebar.Accordion] Use Sidebar.Section with title, icon, and variant="collapsible" for a top-level disclosure. An Accordion outside a titled Section is only shown when the sidebar is expanded.',
        )
      }
      if (!visualIsExpanded) return null
    }
    return (
      <GroupedContext value>
        <Disclosure open={open} onOpenChange={setOpen}>
          <div className={className} data-component="Sidebar.Accordion">
            <div className={clsx(styles.Header, actionStyles.actionRow, actionStyles.disclosureRow)}>
              <Disclosure.Trigger
                render={<button type="button" />}
                ref={ref}
                className={clsx(
                  rowStyles.Row,
                  rowStyles.textLed,
                  styles.Trigger,
                  !open && hasCurrent && rowStyles.current,
                )}
              >
                <span className={rowStyles.Label}>{title}</span>
                {action ? <span aria-hidden className={actionStyles.ActionSpace} /> : null}
                <Disclosure.Chevron className={rowStyles.Chevron} />
              </Disclosure.Trigger>
              {action ? <SidebarAction hover>{action}</SidebarAction> : null}
            </div>
            <DisclosurePanel>
              <div className={styles.AccordionItems}>{children}</div>
            </DisclosurePanel>
          </div>
        </Disclosure>
      </GroupedContext>
    )
  },
  {[sidebarChildKind]: 'container' as const},
)
